import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LoadingOutlined } from '@ant-design/icons';
import { generatePath, useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { Upload } from 'antd';

import { CustomIcon, i18, SIGN_CANCEL_TYPES, translationGroups, trb } from 'src/Utilities';
import { PaymentEditTable, PaymentUploadTable } from './index';
import { Container, ContentLoader, Notification } from 'src/Components';
import { BulkFileActions, PMCActions, UACActiveUser } from 'src/Redux';
import { AlertNotification, Typography } from 'src/shared';
import { PaymentError } from 'src/Containers/Payments/components';
import { routerPath } from '../../../../../Config';

const POLLING_INTERVAL = 3000;

export const PaymentUploading = () => {
  const dispatch = useDispatch();
  const [file, setFile] = useState(null);
  const [warnings, setWarnings] = useState<any[] | null>(null);
  const [AllowToSave, setAllowToSave] = useState(null);
  const [fileGUID, setFileGUID] = useState<string | null>(null);
  const [isSignLater, setSignLater] = useState(false);
  const [paymentErrors, setPaymentErrors] = useState<any[] | null>(null);
  const [isVopPolling, setIsVopPolling] = useState(false);
  const dataUAC = useSelector(UACActiveUser);
  const isBusiness = dataUAC?.Type?.toLowerCase() === 'b';
  const navigate = useNavigate();

  const { sendSUPR, UPR, send, sendUPR, errors, PUPR, sendPUPR, SUPR } = useSelector((state: any) => state.PMCReducers);
  const { FilesList, loading, error } = useSelector((state: any) => state.BulkFileReducer);

  const File = FilesList.find((item: any) => item.FileGuid === fileGUID);

  const VopPassed = File?.VopPassed;

  useEffect(() => {
    if (UPR?.FileGuid) {
      dispatch(BulkFileActions.getBulkPaymentsFileDetails(UPR.FileGuid as string) as any);
    }
  }, [UPR?.FileGuid]);

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (!VopPassed && File?.FileGuid && File?.ReturnValue?.length) {
      setIsVopPolling(true);
      interval = setInterval(() => {
        dispatch(BulkFileActions.getBulkPaymentsFileDetails(UPR?.FileGuid as string) as any);
      }, POLLING_INTERVAL);
    } else {
      setIsVopPolling(false);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [VopPassed, File?.FileGuid, UPR?.FileGuid, File?.ReturnValue?.length]);

  useEffect(
    () => () => {
      dispatch(PMCActions.clearUPR() as any);
      dispatch(PMCActions.clearPUPR() as any);
      dispatch(PMCActions.clearSUPR() as any);
    },
    []
  );

  useEffect(() => {
    if (UPR && sendUPR === false && send === false) {
      if (UPR?.IsSuccessfull) {
        if (UPR?.ErrorCaptions?.[0]?.ErrorCode) {
          setWarnings(UPR?.ErrorCaptions);
        }
        setAllowToSave(UPR?.AllowToSave);
        setPaymentErrors(null);
      } else {
        Notification({
          type: 'error',
          message: i18.NotificationTitles.FileUploadError,
          noTranslateDescription: true,
          description: <span dangerouslySetInnerHTML={{ __html: trb(i18.NotificationMessages.FileUploadError) }}></span>,
        });
      }
    }
  }, [UPR]);

  useEffect(() => {
    if (PUPR && sendPUPR === false && send === false) {
      if (PUPR?.IsSuccessfull) {
        Notification({
          type: 'success',
          message: i18.NotificationTitles.SavedSuccess,
          description: i18.NotificationMessages.SavedSuccess,
        });
        if (!fileGUID) {
          dispatch(PMCActions.clearPUPR() as any);
          dispatch(PMCActions.clearSUPR() as any);
          dispatch(PMCActions.clearUPR() as any);
        }
      } else {
        Notification({
          type: 'error',
          message: i18.NotificationTitles.SavedError,
          description: i18.NotificationMessages.SavedError,
        });
      }
    }
  }, [PUPR]);

  const handleUpload = (info: any) => {
    const formData = new FormData();
    formData.append('file', info.file);

    dispatch(PMCActions.postUPR(formData) as any);
  };

  useEffect(() => {
    if (SUPR && sendSUPR === false && send === false) {
      if (SUPR?.IsSuccessfull) {
        setAllowToSave(SUPR?.AllowToSave);
        const paymentList = SUPR?.PaymentsList,
          errorInPaymentList =
            paymentList && paymentList.length
              ? paymentList.reduce(
                  (summStr: any, rowItem: any, rowIndex: any) => {
                    return rowItem.Errors && rowItem.Errors.length ? (
                      <>
                        {summStr}
                        {rowItem.Errors.reduce(
                          (errorStr: any, item: any) => {
                            return (
                              <>
                                {errorStr}
                                <div>
                                  {rowIndex}: {item}
                                </div>
                              </>
                            );
                          },
                          <></>
                        )}
                      </>
                    ) : (
                      summStr
                    );
                  },
                  <>{trb(i18.NotificationMessages.UploadPaymentErrorInPaymentList)}</>
                )
              : null,
          errorInPaymentListCount =
            paymentList && paymentList.length
              ? paymentList.filter((rowItem: any) => {
                  return rowItem.Errors && rowItem.Errors.length > 0;
                }).length
              : 0;

        if (errorInPaymentListCount) {
          Notification({
            type: 'error',
            message: i18.NotificationTitles.UpdateError,
            description: errorInPaymentList,
            noTranslateDescription: true,
          });
        } else {
          Notification({
            type: 'success',
            message: i18.NotificationTitles.UpdateSuccess,
            description: i18.NotificationMessages.UpdateSuccess,
          });

          if (SUPR?.ErrorCaptions?.[0]?.ErrorCode) {
            setWarnings(SUPR?.ErrorCaptions);
          }
        }
      } else {
        Notification({
          type: 'error',
          message: i18.NotificationTitles.UpdateError,
          description: i18.NotificationMessages.UpdateError,
        });
      }
    }
  }, [SUPR]);

  const isFileSaved = fileGUID && FilesList.findIndex((item: any) => item.FileGuid === fileGUID) !== -1;

  const UpdateFile = (values: any) => {
    dispatch(PMCActions.postSUPR(values) as any);
  };

  const handleSave = async () => {
    setSignLater(true);
    const UploadFileID = UPR?.UploadFileID;
    await dispatch(PMCActions.postPUPR({ FileID: UploadFileID.toString() }) as any);

    navigate(`${routerPath.payments}/${routerPath.paymentUploading}`);
  };

  const handlePreview = async () => {
    setSignLater(false);
    const UploadFileID = UPR?.UploadFileID;
    setFileGUID(UploadFileID);

    await dispatch(PMCActions.postPUPR({ FileID: UploadFileID.toString() }) as any);

    navigate(generatePath(`${routerPath.payments}/${routerPath.unsigned}/${routerPath.unsignedFiles}/${routerPath.unsignedFileDetails}`, { FileGuid: UploadFileID }));
  };

  const props = {
    accept: '.xml',
    multiple: false,
    disabled: send || sendUPR,
    onRemove: () => {
      setFile(null);
    },
    beforeUpload: (fileParam: any) => {
      setFile(fileParam);
      return false;
    },
    onChange: (info: any) => handleUpload(info),
    file,
  };

  const getData = () => {
    const returnValue = SUPR ?? UPR ?? PUPR;

    if (!File?.ReturnValue?.length) {
      return returnValue;
    }

    const newList = File?.ReturnValue.map((item: any) => ({
      Amount: item?.Amount?.[0]?.Value,
      Currency: item?.Amount?.[0]?.Currency,
      PaymentID: item.ID,
      DebtorName: item?.SenderName,
      DebtorIBAN: item?.SenderAccount,
      CreditorName: item?.ReceiverName,
      CreditorIBAN: item?.ReceiverAccount,
      PaymentPurpose: item?.Purpose,
      UniqPayCode: item?.UniqPayCode,
      AmountFormatted: item?.Amount?.[0]?.Text,
      VopResponseType: item?.VopResponseType,
    }));

    return {
      ...returnValue,
      PaymentsList: newList,
    };
  };

  const data = getData();

  if (sendSUPR) {
    return <ContentLoader />;
  }

  const requiredKYCUpdate = paymentErrors?.some((item: any) => item.ErrorCode === 'KycMustUpdated');

  return (
    <div>
      <Typography.Title
        level={1}
        style={{ fontSize: '1rem', marginBottom: '20px' }}
        className="eb_content_subtitle fw-normal"
      >
        {trb(i18.ContentMenu.PaymentUploading)}
      </Typography.Title>
      <Container cardClassName={classNames('payment-uploading-card', { 'pb-0': data })}>
        {isVopPolling && (
          <div className="mb-4">
            <AlertNotification
              alertClassName="w-100"
              isLoading={isVopPolling}
              message={trb(i18.PaymentBulk.PaymentsInReviewTitle)}
              description={trb(i18.PaymentBulk.PaymentsInReview)}
              type="warning"
            />
          </div>
        )}
        {(!data || !AllowToSave) && (
          <div className="w-100">
            <Upload.Dragger
              {...props}
              className="mb-5 pb-5"
              showUploadList={false}
            >
              <div className="eb_content_subtitle text-center mx-1 eb_fw_300 text-primary">{trb(i18.Labels.SelectFile)}</div>
              <p className="ant-upload-drag-icon text-primary">
                {send ? (
                  <div
                    role="status"
                    aria-live="polite"
                    aria-busy="true"
                  >
                    <LoadingOutlined aria-hidden="true" />
                  </div>
                ) : (
                  <CustomIcon
                    type="custom"
                    icon="plusIcon"
                  />
                )}
              </p>
            </Upload.Dragger>
          </div>
        )}
        {data && AllowToSave === true && (
          <>
            {isBusiness && (
              <>
                {data?.VopBulkStatus ? (
                  <div className="mb-4">
                    <AlertNotification
                      alertClassName="w-100"
                      message={trb(i18.PaymentBulk.VOPStatusTitleTrue)}
                      description={trb(i18.PaymentBulk.VOPStatusTrue)}
                      type="success"
                    />
                  </div>
                ) : (
                  <div className="mb-4">
                    <AlertNotification
                      alertClassName="w-100"
                      message={trb(i18.PaymentBulk.VOPStatusTitleFalse)}
                      description={trb(i18.PaymentBulk.VOPStatusFalse)}
                      type="warning"
                    />
                  </div>
                )}
              </>
            )}
            {warnings && !isFileSaved && (
              <>
                {warnings.map((item: any, index: number) => (
                  <AlertNotification
                    key={index}
                    message={trb(translationGroups.ErrorCode + item.ErrorCode)}
                    type="warning"
                    alertClassName="py-3 mb-3"
                  />
                ))}
              </>
            )}
            <PaymentError errors={paymentErrors} />
            {error === SIGN_CANCEL_TYPES.PMC10000 && (
              <div className="mb-4">
                <AlertNotification
                  alertClassName="w-100"
                  message=""
                  description={trb(translationGroups.ErrorCode + error)}
                  type="info"
                />
              </div>
            )}
            <PaymentUploadTable
              data={data}
              buttons={[
                {
                  title: i18.Buttons.SafeOperation,
                  func: handleSave,
                  props: {
                    disabled: send || requiredKYCUpdate,
                    loading: isSignLater && send,
                  },
                },
                {
                  title: !isFileSaved ? i18.Buttons.ApproveUploadedPayments : i18.Buttons.Approve,
                  func: handlePreview,
                  props: {
                    type: 'primary',
                    disabled: send || requiredKYCUpdate || (isFileSaved && !VopPassed),
                    loading: (!isSignLater && send) || loading,
                  },
                },
              ].filter((item) => (item.title === i18.Buttons.SafeOperation ? !isFileSaved : true))}
            />
          </>
        )}
        {data && AllowToSave === false && (
          <>
            {warnings && !isFileSaved && (
              <AlertNotification
                message={trb(i18.ErrorTitles.Warning)}
                type="warning"
                alertClassName="py-3 mb-3"
                description={<ul className="mb-0">{warnings?.map((item: any, index: number) => <li key={index}>{trb(translationGroups.ErrorCode + item.ErrorCode)}</li>)}</ul>}
              />
            )}
            {errors && (
              <AlertNotification
                message={trb(i18.ErrorTitles.Warning)}
                type="error"
                alertClassName="py-3 mb-3"
                description={<ul className="mb-0">{errors?.map((item: any, index: number) => <li key={index}>{trb(translationGroups.ErrorCode + item.ErrorCode)}</li>)}</ul>}
              />
            )}
            <PaymentEditTable
              data={data}
              submit={UpdateFile}
              buttons={[
                {
                  title: i18.Buttons.UpdateFile,
                  props: {
                    type: 'primary',
                    htmlType: 'submit',
                    disabled: send,
                    loading: send,
                  },
                },
              ]}
            />
          </>
        )}
      </Container>
    </div>
  );
};
